import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReadingEditor = ({ reading, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    day: '',
    title: '',
    content: '',
    isPublished: false
  });
  const [isPreview, setIsPreview] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (reading) {
      setFormData({
        day: reading?.day || '',
        title: reading?.title || '',
        content: reading?.content || '',
        isPublished: reading?.isPublished || false
      });
    } else {
      setFormData({
        day: '',
        title: '',
        content: '',
        isPublished: false
      });
    }
    setErrors({});
    setIsPreview(false);
  }, [reading, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.day || formData?.day < 1 || formData?.day > 42) {
      newErrors.day = 'Day must be between 1 and 42';
    }
    
    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData?.content?.trim()) {
      newErrors.content = 'Content is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        ...reading,
        ...formData,
        day: parseInt(formData?.day)
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const formatContent = (content) => {
    return content?.split('\n')?.map((paragraph, index) => (
      <p key={index} className="mb-4 last:mb-0">
        {paragraph}
      </p>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-100 p-4">
      <div className="bg-card border border-border rounded-lg shadow-gentle-active max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="BookOpen" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                {reading ? 'Edit Reading' : 'Add New Reading'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {reading ? `Day ${reading?.day}` : 'Create daily curriculum content'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName={isPreview ? 'Edit' : 'Eye'}
              onClick={() => setIsPreview(!isPreview)}
            >
              {isPreview ? 'Edit' : 'Preview'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onCancel}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!isPreview ? (
            /* Edit Mode */
            (<div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Input
                  label="Day Number"
                  type="number"
                  min="1"
                  max="42"
                  value={formData?.day}
                  onChange={(e) => handleInputChange('day', e?.target?.value)}
                  error={errors?.day}
                  placeholder="1-42"
                  required
                />
                
                <div className="flex items-center space-x-3 pt-8">
                  <input
                    type="checkbox"
                    id="isPublished"
                    checked={formData?.isPublished}
                    onChange={(e) => handleInputChange('isPublished', e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring focus:ring-2"
                  />
                  <label htmlFor="isPublished" className="text-sm font-medium text-foreground">
                    Publish immediately
                  </label>
                </div>
              </div>
              <Input
                label="Reading Title"
                type="text"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                error={errors?.title}
                placeholder="Enter the reading title"
                required
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Reading Content <span className="text-error">*</span>
                </label>
                <textarea
                  value={formData?.content}
                  onChange={(e) => handleInputChange('content', e?.target?.value)}
                  placeholder="Enter the daily reading content..."
                  rows={12}
                  className={`w-full px-3 py-2 border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none ${
                    errors?.content ? 'border-error' : 'border-border'
                  }`}
                />
                {errors?.content && (
                  <p className="text-sm text-error">{errors?.content}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Use line breaks to separate paragraphs. Rich formatting will be applied automatically.
                </p>
              </div>
            </div>)
          ) : (
            /* Preview Mode */
            (<div className="max-w-2xl mx-auto">
              <div className="bg-muted/30 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 text-primary rounded-lg font-heading font-semibold">
                      {formData?.day}
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      formData?.isPublished 
                        ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      {formData?.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Day {formData?.day} of 42
                  </span>
                </div>
                
                <h1 className="text-2xl font-heading font-bold text-foreground mb-6">
                  {formData?.title || 'Untitled Reading'}
                </h1>
                
                <div className="prose prose-sm max-w-none text-foreground">
                  {formData?.content ? formatContent(formData?.content) : (
                    <p className="text-muted-foreground italic">No content yet...</p>
                  )}
                </div>
              </div>
            </div>)
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {reading ? 'Last modified: ' + new Date()?.toLocaleDateString() : 'New reading'}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Save"
              iconPosition="left"
            >
              {reading ? 'Update Reading' : 'Create Reading'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingEditor;